import { Injectable } from '@angular/core';
import * as pdfMake from 'pdfmake/build/pdfmake';
import { ClientTemplateService } from '../client-template.service';
import { InvoiceData } from './models/invoice-data.interface';
import { TemplateRegistryService } from './template-registry.service';

@Injectable({
  providedIn: 'root',
})
export class PdfGenerationService {
  constructor(
    private templateRegistry: TemplateRegistryService,
    private clientTemplateService: ClientTemplateService,
  ) { }

  /**
   * Inject compress:false into a pdfmake document definition.
   *
   * The RangeError "Cannot create final Buffer > 0x7fffffff bytes" is caused
   * by pdfmake's internal zlib Inflate decompressing the embedded font streams
   * into a single in-memory buffer that exceeds Node/browser limits (~2 GB).
   *
   * Setting compress:false bypasses the Deflate/Inflate cycle for font data
   * and produces a slightly larger but stable PDF file.  File size difference
   * in practice is < 5% for typical A4 invoices.
   */
  private withNoCompress(def: any): any {
    return { ...def, compress: false };
  }

  /**
   * Generate and download invoice PDF
   * @param invoiceData - Invoice data to generate PDF from
   * @param templateName - Optional template name (overrides ClientSettings)
   * @param filename - Optional custom filename
   */
  generateInvoicePdf(
    invoiceData: InvoiceData,
    templateName?: string,
    filename?: string,
  ): void {
    console.log(templateName)
    // Get template name from parameter or ClientSettings
    const selectedTemplate = templateName || this.getTemplateFromSettings();
    console.log('--- Full Invoice API Data ---', invoiceData);
    // Get the template renderer
    const template = this.templateRegistry.getTemplate(selectedTemplate);

    console.log(
      `Generating PDF using template: ${template.templateName} (${template.pageSize})`,
    );

    // Generate PDF definition using the template
    const pdfDefinition = template.generatePdfDefinition(invoiceData);

    // Generate filename
    const pdfFilename = filename || this.generateFilename(invoiceData);

    // Create and download PDF (compress:false prevents Buffer overflow)
    pdfMake.createPdf(this.withNoCompress(pdfDefinition) as any).download(pdfFilename);
  }

  /**
   * Generate and open invoice PDF in new tab
   * Uses manual blob management to keep PDF accessible longer
   */
  openInvoicePdf(invoiceData: InvoiceData, templateName?: string): void {
    console.log(templateName)
    const selectedTemplate = templateName || this.getTemplateFromSettings();
    const template = this.templateRegistry.getTemplate(selectedTemplate);

    console.log(
      `Opening PDF using template: ${template.templateName} (${template.pageSize})`,
    );
     console.log('--- Full Invoice API Data ---', invoiceData);

    const pdfDefinition = template.generatePdfDefinition(invoiceData);
    // compress:false prevents "Cannot create final Buffer > 0x7fffffff bytes"
    const pdfDocGenerator = pdfMake.createPdf(this.withNoCompress(pdfDefinition) as any);

    pdfDocGenerator.getBlob((blob: Blob) => {
      const blobUrl = URL.createObjectURL(blob);

      const newWindow = window.open(blobUrl, '_blank');

      if (!newWindow) {
        console.warn('PDF popup blocked by browser, trying download fallback');
        const filename = this.generateFilename(invoiceData);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename;
        link.click();
        URL.revokeObjectURL(blobUrl);
      } else {
        newWindow.addEventListener('beforeunload', () => {
          URL.revokeObjectURL(blobUrl);
          console.log('PDF blob URL revoked after window closed');
        });
      }
    });
  }

  /**
   * Generate PDF as blob (for printing or other purposes)
   */
  async generatePdfBlob(
    invoiceData: InvoiceData,
    templateName?: string,
  ): Promise<Blob> {
    const selectedTemplate = templateName || this.getTemplateFromSettings();
    const template = this.templateRegistry.getTemplate(selectedTemplate);

    console.log(
      `Generating PDF blob using template: ${template.templateName} (${template.pageSize})`,
    );

    const pdfDefinition = template.generatePdfDefinition(invoiceData);

    return new Promise((resolve, reject) => {
      pdfMake.createPdf(this.withNoCompress(pdfDefinition) as any).getBlob((blob) => {
        resolve(blob);
      });
    });
  }

  async openAndGetPdfBlob(
    invoiceData: InvoiceData,
    templateName?: string,
  ): Promise<Blob> {
    const selectedTemplate = templateName || this.getTemplateFromSettings();
    const template = this.templateRegistry.getTemplate(selectedTemplate);

    const pdfDefinition = template.generatePdfDefinition(invoiceData);

    return new Promise((resolve, reject) => {
      const pdfDocGenerator = pdfMake.createPdf(this.withNoCompress(pdfDefinition) as any);

      pdfDocGenerator.getBlob((blob: Blob) => {
        const blobUrl = URL.createObjectURL(blob);

        const newWindow = window.open(blobUrl, '_blank');

        if (!newWindow) {
          const filename = this.generateFilename(invoiceData);
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = filename;
          link.click();
          URL.revokeObjectURL(blobUrl);
        } else {
          newWindow.addEventListener('beforeunload', () => {
            URL.revokeObjectURL(blobUrl);
          });
        }

        resolve(blob);
      });
    });
  }

  /**
   * Print invoice PDF directly
   */
  printInvoicePdf(invoiceData: InvoiceData, templateName?: string): void {
    const selectedTemplate = templateName || this.getTemplateFromSettings();
    const template = this.templateRegistry.getTemplate(selectedTemplate);

    console.log(
      `Printing PDF using template: ${template.templateName} (${template.pageSize})`,
    );

    const pdfDefinition = template.generatePdfDefinition(invoiceData);

    pdfMake.createPdf(this.withNoCompress(pdfDefinition) as any).print();
  }

  /**
   * Get template name from Client Template Service
   * Uses the new /api/v1/order/getclienttemplate/<clientid> API
   */
  private getTemplateFromSettings(): string {
    try {
      const templateName = this.clientTemplateService.getDefaultTemplateName();
      console.log(`Template from client template service: ${templateName}`);
      return templateName;
    } catch (error) {
      console.warn(
        'Error getting template from client template service, using default',
        error,
      );
      return 'default';
    }
  }

  /**
   * Generate filename for PDF
   */
  private generateFilename(data: InvoiceData): string {
    const type = data.invoiceType || 'invoice';
    const number = data.invoiceNumber || Date.now();
    const timestamp = new Date().getTime();
    return `${type}_${number}_${timestamp}.pdf`;
  }

  /**
   * Get list of available templates
   */
  getAvailableTemplates(): string[] {
    return this.templateRegistry.getTemplateNames();
  }

  /**
   * Get template information
   */
  getTemplateInfo(templateName: string) {
    return this.templateRegistry.getTemplateInfo(templateName);
  }

  /**
   * Check if template exists
   */
  hasTemplate(templateName: string): boolean {
    return this.templateRegistry.hasTemplate(templateName);
  }
}
